import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useViewQuota } from "@/hooks/queries/useViewQuota";
import { useUnlockPage } from "@/hooks/mutations/useUnlockPage";
import { useAnalyzesOnUnlock } from "@/hooks/useQualificationAnalysis";
import { useUpsell } from "@/providers/UpsellProvider";
import type { ContractingItem } from "@/types/domain";

const views = (count: number) => `${count} ${count === 1 ? "vista" : "vistas"}`;

/**
 * "Desbloquear las de esta página", arriba del listado: una vista por cada
 * bloqueada, en el orden de la página, y las cruza si se puede. Si no alcanzan las
 * vistas desbloquea las primeras y lo avisa antes y después. No aparece sin
 * bloqueadas ni con vistas ilimitadas.
 */
export function UnlockPageButton({ items }: { items: ContractingItem[] }) {
  const navigate = useNavigate();
  const openUpsell = useUpsell();
  const { data: quota } = useViewQuota();
  const analyzesOnUnlock = useAnalyzesOnUnlock();
  const unlockPage = useUnlockPage();
  const [confirming, setConfirming] = useState(false);

  const locked = items.filter((item) => item.isLocked);
  if (locked.length === 0 || !quota || quota.unlimited) return null;

  const willAnalyze = locked.some(analyzesOnUnlock);
  const affordable = Math.min(locked.length, quota.remaining);
  const verb = willAnalyze ? "Desbloquear y analizar" : "Desbloquear";

  function confirm() {
    unlockPage.mutate(
      { contractingIds: locked.map((item) => item.contracting_id) },
      {
        onSuccess: (result) => {
          setConfirming(false);
          if (result.unlocked < result.requested) {
            toast(
              `Desbloqueamos ${result.unlocked} de ${result.requested}: eran tus últimas vistas.`,
              { action: { label: "Ver planes", onClick: () => navigate("/settings/plan") } },
            );
          }
        },
      },
    );
  }

  return (
    <div className="flex justify-end">
      <Button
        variant="outline"
        size="sm"
        className="h-auto cursor-pointer whitespace-normal py-2 text-left"
        onClick={() => (quota.remaining === 0 ? openUpsell("no-views") : setConfirming(true))}
      >
        <Lock className="size-3.5" />
        {verb} las {locked.length} de esta página · {views(locked.length)}
      </Button>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿{verb} las {locked.length} de esta página?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {affordable < locked.length
                ? `Te quedan ${views(quota.remaining)}: desbloquearemos las primeras ${affordable} de la página, en su orden, y te quedarás sin vistas.`
                : `Vas a usar ${views(locked.length)}. Te quedan ${quota.remaining}.`}
              {willAnalyze &&
                " Las obras con el registro abierto se analizarán con tus documentos."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                // Se cierra al terminar: así el botón muestra que está trabajando.
                event.preventDefault();
                confirm();
              }}
              disabled={unlockPage.isPending}
            >
              {unlockPage.isPending ? "Desbloqueando…" : `Desbloquear ${affordable}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
